/**
 * SlimFormCapture Component
 *
 * This component allows users to capture 3 photos for the SlimForm submission process:
 * 1. Overview photo (with optional bounding box)
 * 2. Manufacturer plate photo (optional)
 * 3. Asset tag photo (required)
 */

import {
	CheckOutlined,
	CloseOutlined,
	DeleteOutlined,
	InboxOutlined,
} from "@ant-design/icons";
import {
	Button,
	Card,
	Input,
	Modal,
	Select,
	Space,
	Steps,
	Upload,
	message,
} from "antd";
import type { RcFile } from "antd/es/upload";
import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";

const { Dragger } = Upload;
const { TextArea } = Input;
import { CORE_URL } from "@/constants/api";
import { VITE_CORE_API_URL } from "@/constants/env";
import { auth } from "@/services/auth-service";
import {
	type BoundingBox,
	createSlimFormSubmission,
} from "@/services/slim-form-service";
import { uploadAssetPhoto } from "@/services/storage-service";
import { useUserStore } from "@/stores/user-store";
import { isHeicFile, processImageFile } from "@/utils/heic-converter";
import "./SlimFormCapture.scss";

const { Step } = Steps;

interface PhotoCapture {
	file?: Blob;
	preview?: string;
	uploaded?: boolean;
	url?: string;
}

export const SlimFormCapture: React.FC = () => {
	const navigate = useNavigate();
	const { user } = useUserStore();
	const locationState = useLocation();

	// State
	const [currentStep, setCurrentStep] = useState(0);
	const [selectedLocation, setSelectedLocation] = useState<number | null>(null);
	const [selectedLocationName, setSelectedLocationName] = useState<string>("");
	const [overviewPhoto, setOverviewPhoto] = useState<PhotoCapture | null>(null);
	const [manufacturerPhoto, setManufacturerPhoto] =
		useState<PhotoCapture | null>(null);
	const [assetTagPhoto, setAssetTagPhoto] = useState<PhotoCapture | null>(null);
	const [additionalComment, setAdditionalComment] = useState<string>("");
	const [uploading, setUploading] = useState(false);
	const [submitting, setSubmitting] = useState(false);
	const [locations, setLocations] = useState<any[]>([]);
	const [loadingLocations, setLoadingLocations] = useState(true);
	const [skipManufacturerModalVisible, setSkipManufacturerModalVisible] =
		useState(false);
	const [skipManufacturerReason, setSkipManufacturerReason] = useState("");
	const [recentlySubmitted, setRecentlySubmitted] = useState(false);

	// Vision Tag state
	const [visionTagMode, setVisionTagMode] = useState(false);
	const [visionTagModalVisible, setVisionTagModalVisible] = useState(false);
	const [visionTagStep, setVisionTagStep] = useState(0); // 0: bounding box, 1: left photo, 2: right photo
	const [visionTagBoundingBox, setVisionTagBoundingBox] =
		useState<BoundingBox | null>(null);
	const [virtualAssetTag, setVirtualAssetTag] = useState<string | null>(null);
	const [visionTagLeftPhoto, setVisionTagLeftPhoto] =
		useState<PhotoCapture | null>(null);
	const [visionTagRightPhoto, setVisionTagRightPhoto] =
		useState<PhotoCapture | null>(null);
	const [visionTagGeneratedImage, setVisionTagGeneratedImage] = useState<{
		blob: Blob;
		preview: string;
	} | null>(null);
	const [isDrawing, setIsDrawing] = useState(false);
	const [startPoint, setStartPoint] = useState<{ x: number; y: number } | null>(
		null,
	);
	const canvasRef = React.useRef<HTMLCanvasElement>(null);
	const imageRef = React.useRef<HTMLImageElement>(null);
	
	// GPS state for Visual Tag 2.0
	const [gpsLocation, setGpsLocation] = useState<{
		latitude: number;
		longitude: number;
		accuracy: number;
	} | null>(null);
	const [capturingGPS, setCapturingGPS] = useState(false);

	const loadLocations = async () => {
		setLoadingLocations(true);
		try {
			const token = await auth.currentUser?.getIdToken();
			const url = `${VITE_CORE_API_URL}${CORE_URL}/location`;

			const response = await fetch(url, {
				method: "GET",
				headers: {
					Authorization: `Bearer ${token}`,
				},
			});

			if (!response.ok) {
				throw new Error("Failed to fetch locations");
			}

			const result = await response.json();
			setLocations(result.data || []);

			// After loading locations, try to get nearest location using geolocation
			// Skip if a location was pre-selected from Command Center navigation state
			const navState = locationState?.state as any;
			const hasPreselected = navState?.preselectedLocationId && navState?.preselectedLocationName;
			if (token && !hasPreselected) {
				await getNearestLocation(token);
			}
		} catch (error) {
			console.error("Error loading locations:", error);
			message.error("Failed to load locations");
		} finally {
			setLoadingLocations(false);
		}
	};

	/**
	 * Gets approximate location using IP-based geolocation
	 */
	const getLocationFromIP = async (token: string) => {
		try {
			// Use ipapi.co for IP-based geolocation (free, no API key needed)
			const ipResponse = await fetch("https://ipapi.co/json/", {
				method: "GET",
			});

			if (!ipResponse.ok) {
				return;
			}

			const ipData = await ipResponse.json();
			const { latitude, longitude } = ipData;

			if (!latitude || !longitude) {
				return;
			}

			// Search for nearest locations using IP coordinates
			const geoUrl = `${VITE_CORE_API_URL}${CORE_URL}/location/geo/search?latitude=${latitude}&longitude=${longitude}`;

			const geoResponse = await fetch(geoUrl, {
				method: "GET",
				headers: {
					Authorization: `Bearer ${token}`,
				},
			});

			if (!geoResponse.ok) {
				return;
			}

			const geoResult = await geoResponse.json();
			const nearestLocations = geoResult.data || [];

			if (nearestLocations.length > 0) {
				const nearest = nearestLocations[0];
				setSelectedLocation(nearest.id);
				setSelectedLocationName(nearest.address1 || nearest.name || "Unknown");
				message.success(
					`Auto-selected nearest location: ${nearest.name || nearest.address1}`,
				);
			}
		} catch (error: any) {
			// Silently fail - user can manually select location
		}
	};

	/**
	 * Gets user's current location and finds nearest org location
	 */
	const getNearestLocation = async (token: string) => {
		// Check if geolocation is supported
		if (!navigator.geolocation) {
			await getLocationFromIP(token);
			return;
		}

		try {
			// Get user's current position with relaxed settings
			const position = await new Promise<GeolocationPosition>(
				(resolve, reject) => {
					navigator.geolocation.getCurrentPosition(resolve, reject, {
						enableHighAccuracy: false, // Use network/wifi positioning for speed
						timeout: 15000, // 15 second timeout for GPS
						maximumAge: 600000, // Accept cached position up to 10 minutes old
					});
				},
			);

			const { latitude, longitude } = position.coords;

			// Search for nearest locations
			const geoUrl = `${VITE_CORE_API_URL}${CORE_URL}/location/geo/search?latitude=${latitude}&longitude=${longitude}`;

			const geoResponse = await fetch(geoUrl, {
				method: "GET",
				headers: {
					Authorization: `Bearer ${token}`,
				},
			});

			if (!geoResponse.ok) {
				return;
			}

			const geoResult = await geoResponse.json();
			const nearestLocations = geoResult.data || [];

			if (nearestLocations.length > 0) {
				const nearest = nearestLocations[0];
				setSelectedLocation(nearest.id);
				setSelectedLocationName(nearest.address1 || nearest.name || "Unknown");
				message.success(
					`Auto-selected nearest location: ${nearest.name || nearest.address1}`,
				);
			}
		} catch (error: any) {
			// Fallback to IP-based geolocation
			await getLocationFromIP(token);
		}
	};

	/**
	 * Loads locations for the organization
	 */
	useEffect(() => {
		let isMounted = true;

		const load = async () => {
			if (isMounted) {
				await loadLocations();
			}
		};

		load();

		return () => {
			isMounted = false;
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []); // Empty deps - only run once on mount

	// Pre-select location from Command Center navigation state (skip step 0)
	useEffect(() => {
		const navState = locationState?.state as any;
		if (navState?.preselectedLocationId && navState?.preselectedLocationName) {
			setSelectedLocation(navState.preselectedLocationId);
			setSelectedLocationName(navState.preselectedLocationName);
			setCurrentStep(1); // Skip to overview photo step
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []); // Only run once on mount

	/**
	 * Handles photo selection
	 */
	const handlePhotoSelect = async (
		file: RcFile,
		type: "overview" | "manufacturer" | "assetTag",
	) => {
		try {
			// Show loading message if HEIC conversion is needed
			if (isHeicFile(file)) {
				message.loading({
					content: "Converting HEIC image...",
					key: `convert-${type}`,
					duration: 0,
				});
			}

			// Process the file (convert HEIC to JPEG if needed)
			const processedFile = await processImageFile(file);

			// Destroy loading message if it was shown
			if (isHeicFile(file)) {
				message.destroy(`convert-${type}`);
				message.success("Image converted successfully");
			}

			// Create preview
			const reader = new FileReader();
			reader.onload = (e) => {
				const preview = e.target?.result as string;
				const photoData: PhotoCapture = {
					file: processedFile,
					preview,
					uploaded: false,
				};

				switch (type) {
					case "overview":
						setOverviewPhoto(photoData);
						break;
					case "manufacturer":
						setManufacturerPhoto(photoData);
						break;
					case "assetTag":
						setAssetTagPhoto(photoData);
						break;
				}
			};
			reader.readAsDataURL(processedFile);
		} catch (error) {
			console.error("Error processing photo:", error);
			message.error(
				error instanceof Error ? error.message : "Failed to process image",
			);
		}

		return false; // Prevent auto upload
	};

	/**
	 * Removes a photo
	 */
	const handlePhotoRemove = (
		type: "overview" | "manufacturer" | "assetTag",
	) => {
		switch (type) {
			case "overview":
				setOverviewPhoto(null);
				break;
			case "manufacturer":
				setManufacturerPhoto(null);
				break;
			case "assetTag":
				setAssetTagPhoto(null);
				break;
		}
	};

	/**
	 * Generates a virtual asset tag ID
	 */
	const generateVirtualTag = (): string => {
		const timestamp = Date.now().toString(36);
		const randomStr = Math.random().toString(36).substring(2, 7).toUpperCase();
		return `VT-${timestamp}-${randomStr}`;
	};

	/**
	 * Opens Vision Tag modal
	 */
	const handleOpenVisionTag = () => {
		if (!overviewPhoto?.preview) {
			message.error("Overview photo is required for Vision Tag");
			return;
		}
		setVisionTagStep(0);
		setVisionTagBoundingBox(null);
		setVisionTagLeftPhoto(null);
		setVisionTagRightPhoto(null);
		setVisionTagGeneratedImage(null);
		setVisionTagModalVisible(true);
	};

	/**
	 * Handles canvas mouse down - start drawing bounding box
	 */
	const handleCanvasMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
		const canvas = canvasRef.current;
		if (!canvas) return;

		const rect = canvas.getBoundingClientRect();
		const x = e.clientX - rect.left;
		const y = e.clientY - rect.top;

		setIsDrawing(true);
		setStartPoint({ x, y });
	};

	/**
	 * Handles canvas mouse move - draw bounding box preview
	 */
	const handleCanvasMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
		if (!isDrawing || !startPoint) return;

		const canvas = canvasRef.current;
		const image = imageRef.current;
		if (!canvas || !image) return;

		const ctx = canvas.getContext("2d");
		if (!ctx) return;

		const rect = canvas.getBoundingClientRect();
		const currentX = e.clientX - rect.left;
		const currentY = e.clientY - rect.top;

		// Redraw image
		ctx.clearRect(0, 0, canvas.width, canvas.height);
		ctx.drawImage(image, 0, 0, canvas.width, canvas.height);

		// Draw bounding box
		const width = currentX - startPoint.x;
		const height = currentY - startPoint.y;

		ctx.strokeStyle = "#1890ff";
		ctx.lineWidth = 3;
		ctx.strokeRect(startPoint.x, startPoint.y, width, height);

		// Draw semi-transparent fill
		ctx.fillStyle = "rgba(24, 144, 255, 0.1)";
		ctx.fillRect(startPoint.x, startPoint.y, width, height);
	};

	/**
	 * Handles canvas mouse up - finalize bounding box
	 */
	const handleCanvasMouseUp = (e: React.MouseEvent<HTMLCanvasElement>) => {
		if (!isDrawing || !startPoint) return;

		const canvas = canvasRef.current;
		const image = imageRef.current;
		if (!canvas || !image) return;

		const rect = canvas.getBoundingClientRect();
		const endX = e.clientX - rect.left;
		const endY = e.clientY - rect.top;

		// Calculate bounding box in image coordinates
		const scaleX = image.naturalWidth / canvas.width;
		const scaleY = image.naturalHeight / canvas.height;

		const x = Math.round(Math.min(startPoint.x, endX) * scaleX);
		const y = Math.round(Math.min(startPoint.y, endY) * scaleY);
		const width = Math.round(Math.abs(endX - startPoint.x) * scaleX);
		const height = Math.round(Math.abs(endY - startPoint.y) * scaleY);

		// Validate bounding box size (minimum 20x20 pixels)
		if (width < 20 || height < 20) {
			message.warning("Bounding box is too small. Please draw a larger area.");
			setIsDrawing(false);
			setStartPoint(null);
			return;
		}

		setVisionTagBoundingBox({ x, y, width, height });
		setIsDrawing(false);
		setStartPoint(null);
	};

	/**
	 * Confirms bounding box and moves to next Vision Tag step
	 */
	const handleVisionTagNextStep = async () => {
		if (visionTagStep === 0) {
			// Step 0: Validate bounding box and generate image with virtual tag
			if (!visionTagBoundingBox) {
				message.error("Please draw a bounding box around the asset");
				return;
			}

			try {
				// Generate the image with bounding box now while canvas is still available
				const canvas = canvasRef.current;
				const image = imageRef.current;
				if (!canvas || !image) {
					throw new Error("Canvas or image not found");
				}

				// Create a new canvas to draw the final image with bounding box
				const finalCanvas = document.createElement("canvas");
				finalCanvas.width = canvas.width;
				finalCanvas.height = canvas.height;
				const ctx = finalCanvas.getContext("2d");
				if (!ctx) {
					throw new Error("Failed to get canvas context");
				}

				// Draw the image
				ctx.drawImage(image, 0, 0, canvas.width, canvas.height);

				// Calculate bounding box in canvas coordinates
				const scaleX = canvas.width / image.naturalWidth;
				const scaleY = canvas.height / image.naturalHeight;
				const boxX = visionTagBoundingBox.x * scaleX;
				const boxY = visionTagBoundingBox.y * scaleY;
				const boxWidth = visionTagBoundingBox.width * scaleX;
				const boxHeight = visionTagBoundingBox.height * scaleY;

				// Draw bounding box
				ctx.strokeStyle = "#1890ff";
				ctx.lineWidth = 4;
				ctx.strokeRect(boxX, boxY, boxWidth, boxHeight);

				// Draw semi-transparent fill
				ctx.fillStyle = "rgba(24, 144, 255, 0.15)";
				ctx.fillRect(boxX, boxY, boxWidth, boxHeight);

				// Add text label in top-left corner of box
				const tag = generateVirtualTag();
				ctx.font = "bold 16px Arial";
				ctx.fillStyle = "#1890ff";
				ctx.strokeStyle = "#ffffff";
				ctx.lineWidth = 3;
				const textX = boxX + 10;
				const textY = boxY + 25;
				ctx.strokeText(`Vision Tag: ${tag}`, textX, textY);
				ctx.fillText(`Vision Tag: ${tag}`, textX, textY);

				// Convert canvas to blob
				const blob = await new Promise<Blob>((resolve, reject) => {
					finalCanvas.toBlob(
						(blob) => {
							if (blob) {
								resolve(blob);
							} else {
								reject(new Error("Failed to create image blob"));
							}
						},
						"image/jpeg",
						0.95,
					);
				});

				// Create preview URL
				const previewUrl = URL.createObjectURL(blob);

				// Store the generated image and virtual tag
				setVisionTagGeneratedImage({ blob, preview: previewUrl });
				setVirtualAssetTag(tag);

				setVisionTagStep(1);
			} catch (error) {
				console.error("Error generating vision tag image:", error);
				message.error("Failed to generate vision tag image");
			}
		} else if (visionTagStep === 1) {
			// Step 1: Validate left photo
			if (!visionTagLeftPhoto) {
				message.error("Please capture the left side photo");
				return;
			}
			setVisionTagStep(2);
		} else if (visionTagStep === 2) {
			// Step 2: Validate right photo and finalize
			if (!visionTagRightPhoto) {
				message.error("Please capture the right side photo");
				return;
			}
			await handleConfirmVisionTag();
		}
	};

	/**
	 * Goes back one step in Vision Tag flow
	 */
	const handleVisionTagPreviousStep = () => {
		if (visionTagStep > 0) {
			setVisionTagStep(visionTagStep - 1);
		}
	};

	/**
	 * Get GPS location for Visual Tag 2.0
	 * Uses the selected location's GPS coordinates (where assets are)
	 */
	const getLocationGPS = (): { latitude: number; longitude: number; accuracy: number } | null => {
		if (!selectedLocation) return null;
		
		// Find the selected location
		const location = locations.find(loc => loc.id === selectedLocation);
		if (!location) return null;
		
		// Check if location has GPS coordinates
		if (!location.latitude || !location.longitude) {
			return null;
		}
		
		return {
			latitude: location.latitude,
			longitude: location.longitude,
			accuracy: 10, // Use default accuracy for location coordinates (assumed to be precise)
		};
	};

	/**
	 * Captures live GPS from device for Visual Tag 2.0 enrollment
	 * Falls back to location GPS if device GPS unavailable
	 */
	const captureLiveGPS = async (): Promise<void> => {
		setCapturingGPS(true);
		
		// Check if geolocation is supported
		if (!navigator.geolocation) {
			console.warn('Geolocation not supported, falling back to location GPS');
			const locationGPS = getLocationGPS();
			if (locationGPS) {
				setGpsLocation(locationGPS);
				message.warning('Device GPS unavailable. Using location GPS coordinates for Visual Tag 2.0.');
			} else {
				message.error('No GPS available. Visual Tag 2.0 enrollment will be skipped.');
			}
			setCapturingGPS(false);
			return;
		}

		try {
			// Capture live GPS from device
			const position = await new Promise<GeolocationPosition>(
				(resolve, reject) => {
					navigator.geolocation.getCurrentPosition(resolve, reject, {
						enableHighAccuracy: true, // Use GPS for high accuracy
						timeout: 10000, // 10 second timeout
						maximumAge: 0, // Don't use cached position
					});
				},
			);

			const liveGPS = {
				latitude: position.coords.latitude,
				longitude: position.coords.longitude,
				accuracy: position.coords.accuracy,
			};

			setGpsLocation(liveGPS);
			message.success(
				`Live GPS captured (±${liveGPS.accuracy.toFixed(0)}m accuracy) for Visual Tag 2.0`,
			);
		} catch (error) {
			console.error('Error capturing live GPS:', error);
			
			// Fallback to location GPS
			const locationGPS = getLocationGPS();
			if (locationGPS) {
				setGpsLocation(locationGPS);
				message.warning(
					'Could not capture device GPS. Using location GPS coordinates for Visual Tag 2.0.',
				);
			} else {
				message.error('No GPS available. Visual Tag 2.0 enrollment will be skipped.');
			}
		} finally {
			setCapturingGPS(false);
		}
	};

	/**
	 * Confirms Vision Tag and finalizes the process
	 */
	const handleConfirmVisionTag = async () => {
		try {
			// Use the pre-generated image from step 0
			if (!visionTagGeneratedImage) {
				throw new Error("Vision tag image not generated");
			}

			// Set as asset tag photo
			setAssetTagPhoto({
				file: visionTagGeneratedImage.blob,
				preview: visionTagGeneratedImage.preview,
				uploaded: false,
			});

		// Set vision tag mode
		setVisionTagMode(true);
		
		// Capture live GPS from device for Visual Tag 2.0 enrollment
		await captureLiveGPS();
		
		setVisionTagModalVisible(false);
			message.success(
				`Virtual tag generated with left and right photos: ${virtualAssetTag}`,
			);
		} catch (error) {
			console.error("Error finalizing vision tag:", error);
			message.error("Failed to finalize vision tag");
		}
	};

	/**
	 * Handles Vision Tag photo selection (left/right)
	 */
	const handleVisionTagPhotoSelect = async (
		file: RcFile,
		side: "left" | "right",
	) => {
		try {
			const processedFile = await processImageFile(file);
			const reader = new FileReader();
			reader.onload = (e) => {
				const preview = e.target?.result as string;
				const photoData = {
					file: processedFile,
					preview,
					uploaded: false,
				};

				if (side === "left") {
					setVisionTagLeftPhoto(photoData);
				} else {
					setVisionTagRightPhoto(photoData);
				}
			};
			reader.readAsDataURL(processedFile);
		} catch (error) {
			console.error("Error processing Vision Tag photo:", error);
			message.error(
				error instanceof Error ? error.message : "Failed to process image",
			);
		}
		return false;
	};

	/**
	 * Cancels Vision Tag mode
	 */
	const handleCancelVisionTag = () => {
		setVisionTagMode(false);
		setVirtualAssetTag(null);
		setVisionTagBoundingBox(null);
		setVisionTagLeftPhoto(null);
		setVisionTagRightPhoto(null);
		setVisionTagGeneratedImage(null);
		setVisionTagStep(0);
	};

	/**
	 * Validates current step
	 */
	const validateStep = (step: number): boolean => {
		switch (step) {
			case 0:
				if (!selectedLocation) {
					message.error("Please select a location");
					return false;
				}
				return true;
			case 1:
				if (!overviewPhoto) {
					message.error("Please capture an overview photo");
					return false;
				}
				return true;
			case 2:
				// Manufacturer plate is optional
				return true;
			case 3:
				// Asset tag is required
				if (!assetTagPhoto) {
					message.error("Please capture an asset tag photo or use Vision Tag");
					return false;
				}
				return true;
			default:
				return true;
		}
	};

	/**
	 * Moves to next step
	 */
	const handleNext = () => {
		if (validateStep(currentStep)) {
			setCurrentStep(currentStep + 1);
		}
	};

	/**
	 * Moves to previous step
	 */
	const handleBack = () => {
		setCurrentStep(currentStep - 1);
	};

	/**
	 * Handles cancel - navigates back based on where user came from
	 */
	const handleCancel = () => {
		const fromPath = locationState?.state?.from;

		if (fromPath === "/command-center") {
			navigate("/");
		} else if (fromPath?.includes("/admin/quality-check")) {
			navigate("/admin/quality-check");
		} else {
			// Default to Vision Capture (forms) page
			navigate("/forms");
		}
	};

	/**
	 * Handles skipping the manufacturer plate with a reason
	 */
	const handleSkipManufacturerPlate = () => {
		if (!skipManufacturerReason) {
			message.error(
				"Please select a reason for skipping the manufacturer plate",
			);
			return;
		}

		// Append the skip reason to additional comments
		const skipNote = `Manufacturer Plate Skipped: ${skipManufacturerReason}`;
		setAdditionalComment((prev) => {
			if (prev) {
				return `${prev}\n\n${skipNote}`;
			}
			return skipNote;
		});

		// Close modal and move to next step
		setSkipManufacturerModalVisible(false);
		setSkipManufacturerReason("");
		setCurrentStep(3);
	};

	/**
	 * Compute SHA-256 hash of a Blob/File for OCR cache deduplication
	 */
	const hashPhoto = async (blob: Blob): Promise<string> => {
		const buffer = await blob.arrayBuffer();
		const hashBuffer = await crypto.subtle.digest("SHA-256", buffer);
		return Array.from(new Uint8Array(hashBuffer))
			.map((b) => b.toString(16).padStart(2, "0"))
			.join("");
	};

	/**
	 * Submits the SlimForm
	 */
	const handleSubmit = async () => {
		if (!validateStep(currentStep)) return;
		if (!selectedLocation || !overviewPhoto) {
			message.error("Please complete all required photos");
			return;
		}

		setSubmitting(true);

		try {
			// Upload photos to Firebase Storage SEQUENTIALLY to avoid filename collisions
			// Compute SHA-256 hashes in parallel (non-blocking, runs alongside uploads)
			setUploading(true);
			const uploadKey = "upload";
			message.loading({
				content: "Uploading photos...",
				key: uploadKey,
				duration: 0,
			});

			const timestamp = Date.now();

			// Start hash computation in parallel (doesn't block uploads)
			const hashPromises = {
				overview: hashPhoto(overviewPhoto.file!),
				manufacturerPlate: manufacturerPhoto?.file ? hashPhoto(manufacturerPhoto.file) : undefined,
				assetTag: assetTagPhoto?.file ? hashPhoto(assetTagPhoto.file) : undefined,
			};

			// Upload sequentially with unique identifiers
			const overviewUrl = await uploadAssetPhoto(
				overviewPhoto.file!,
				`temp-${timestamp}-overview`,
			);

			const manufacturerUrl = manufacturerPhoto?.file
				? await uploadAssetPhoto(
						manufacturerPhoto.file,
						`temp-${timestamp}-manufacturer`,
					)
				: undefined;

			const assetTagUrl = assetTagPhoto?.file
				? await uploadAssetPhoto(
						assetTagPhoto.file,
						`temp-${timestamp}-assettag`,
					)
				: undefined;

			// Upload Vision Tag photos if present
			const visionTagLeftUrl =
				visionTagMode && visionTagLeftPhoto?.file
					? await uploadAssetPhoto(
							visionTagLeftPhoto.file,
							`temp-${timestamp}-visiontag-left`,
						)
					: undefined;

			const visionTagRightUrl =
				visionTagMode && visionTagRightPhoto?.file
					? await uploadAssetPhoto(
							visionTagRightPhoto.file,
							`temp-${timestamp}-visiontag-right`,
						)
					: undefined;

			// Await all hash computations (should be done by now since uploads are slower)
			const photoHashes: { overview: string; manufacturerPlate?: string; assetTag?: string } = {
				overview: await hashPromises.overview,
				manufacturerPlate: hashPromises.manufacturerPlate ? await hashPromises.manufacturerPlate : undefined,
				assetTag: hashPromises.assetTag ? await hashPromises.assetTag : undefined,
			};

			message.destroy(uploadKey);
			setUploading(false);

			// Create submission
			const submitKey = "submit";
			message.loading({
				content: "Creating submission...",
				key: submitKey,
				duration: 0,
			});

			const submissionData = {
				locationId: selectedLocation,
				overviewPhotoUrl: overviewUrl,
				manufacturerPlatePhotoUrl: manufacturerUrl,
				assetTagPhotoUrl: assetTagUrl,
				additionalComment: additionalComment || undefined,
				boundingBox:
					visionTagMode && visionTagBoundingBox
						? visionTagBoundingBox
						: undefined,
				virtualAssetTag:
					visionTagMode && virtualAssetTag ? virtualAssetTag : undefined,
				visionTagLeftPhotoUrl: visionTagLeftUrl,
				visionTagRightPhotoUrl: visionTagRightUrl,
				photoHashes,
				// GPS data for Visual Tag 2.0 integration
				editedData: visionTagMode && gpsLocation ? {
					gpsLatitude: gpsLocation.latitude.toString(),
					gpsLongitude: gpsLocation.longitude.toString(),
					gpsAccuracy: gpsLocation.accuracy.toString(),
				} : undefined,
			};

			const submission = await createSlimFormSubmission(submissionData);

			message.success({
				content: "Submission created successfully! AI processing has started.",
				key: submitKey,
			});

			// Prevent double-click submissions (short cooldown)
			setRecentlySubmitted(true);
			setTimeout(() => {
				setRecentlySubmitted(false);
			}, 500); // 0.5 second cooldown (just prevents accidental double-clicks)

			// Navigate to queue page after brief delay
			setTimeout(() => {
				navigate("/slim-form/queue");
			}, 500);
		} catch (error) {
			console.error("Error submitting SlimForm:", error);
			message.error(
				error instanceof Error ? error.message : "Failed to submit SlimForm",
			);
		} finally {
			setSubmitting(false);
			setUploading(false);
		}
	};

	/**
	 * Submits the SlimForm and starts a new submission with the same location
	 */
	const handleSubmitAndStartNew = async () => {
		if (!validateStep(currentStep)) return;
		if (!selectedLocation || !overviewPhoto) {
			message.error("Please complete all required photos");
			return;
		}

		setSubmitting(true);

		try {
			// Upload photos to Firebase Storage SEQUENTIALLY to avoid filename collisions
			// Compute SHA-256 hashes in parallel (non-blocking, runs alongside uploads)
			setUploading(true);
			const uploadKey = "upload";
			message.loading({
				content: "Uploading photos...",
				key: uploadKey,
				duration: 0,
			});

			const timestamp = Date.now();

			// Start hash computation in parallel
			const hashPromises = {
				overview: hashPhoto(overviewPhoto.file!),
				manufacturerPlate: manufacturerPhoto?.file ? hashPhoto(manufacturerPhoto.file) : undefined,
				assetTag: assetTagPhoto?.file ? hashPhoto(assetTagPhoto.file) : undefined,
			};

			// Upload sequentially with unique identifiers
			const overviewUrl = await uploadAssetPhoto(
				overviewPhoto.file!,
				`temp-${timestamp}-overview`,
			);

			const manufacturerUrl = manufacturerPhoto?.file
				? await uploadAssetPhoto(
						manufacturerPhoto.file,
						`temp-${timestamp}-manufacturer`,
					)
				: undefined;

			const assetTagUrl = assetTagPhoto?.file
				? await uploadAssetPhoto(
						assetTagPhoto.file,
						`temp-${timestamp}-assettag`,
					)
				: undefined;

			// Upload Vision Tag photos if present
			const visionTagLeftUrl =
				visionTagMode && visionTagLeftPhoto?.file
					? await uploadAssetPhoto(
							visionTagLeftPhoto.file,
							`temp-${timestamp}-visiontag-left`,
						)
					: undefined;

			const visionTagRightUrl =
				visionTagMode && visionTagRightPhoto?.file
					? await uploadAssetPhoto(
							visionTagRightPhoto.file,
							`temp-${timestamp}-visiontag-right`,
						)
					: undefined;

			// Await hash computations
			const photoHashes: { overview: string; manufacturerPlate?: string; assetTag?: string } = {
				overview: await hashPromises.overview,
				manufacturerPlate: hashPromises.manufacturerPlate ? await hashPromises.manufacturerPlate : undefined,
				assetTag: hashPromises.assetTag ? await hashPromises.assetTag : undefined,
			};

			message.destroy(uploadKey);
			setUploading(false);

			// Create submission
			const submitKey = "submit";
			message.loading({
				content: "Creating submission...",
				key: submitKey,
				duration: 0,
			});

			const submissionData = {
				locationId: selectedLocation,
				overviewPhotoUrl: overviewUrl,
				manufacturerPlatePhotoUrl: manufacturerUrl,
				assetTagPhotoUrl: assetTagUrl,
				additionalComment: additionalComment || undefined,
				boundingBox:
					visionTagMode && visionTagBoundingBox
						? visionTagBoundingBox
						: undefined,
				virtualAssetTag:
					visionTagMode && virtualAssetTag ? virtualAssetTag : undefined,
				visionTagLeftPhotoUrl: visionTagLeftUrl,
				visionTagRightPhotoUrl: visionTagRightUrl,
				photoHashes,
			};

			const submission = await createSlimFormSubmission(submissionData);

			message.success({
				content:
					"Submission created! Starting new capture for the same location.",
				key: submitKey,
			});

			// Reset photos, comment, Vision Tag state and go back to overview photo step (step 1)
			// Keep the location selection
			setOverviewPhoto(null);
			setManufacturerPhoto(null);
			setAssetTagPhoto(null);
			setAdditionalComment("");
			setVisionTagMode(false);
			setVirtualAssetTag(null);
			setVisionTagBoundingBox(null);
			setVisionTagLeftPhoto(null);
			setVisionTagRightPhoto(null);
			setVisionTagGeneratedImage(null);
			setVisionTagStep(0);
			setCurrentStep(1);

			// Prevent double-click submissions (short cooldown)
			setRecentlySubmitted(true);
			setTimeout(() => {
				setRecentlySubmitted(false);
			}, 500); // 0.5 second cooldown (just prevents accidental double-clicks)
		} catch (error) {
			console.error("Error submitting SlimForm:", error);
			message.error(
				error instanceof Error ? error.message : "Failed to submit SlimForm",
			);
		} finally {
			setSubmitting(false);
			setUploading(false);
		}
	};

	/**
	 * Renders step content
	 */
	const renderStepContent = () => {
		switch (currentStep) {
			case 0:
				return (
					<div className="step-content">
						<h3>Select Location</h3>
						<p>Choose the location where this asset is located.</p>
						<Select
							showSearch
							placeholder={
								loadingLocations ? "Loading locations..." : "Select a location"
							}
							style={{ width: "100%", maxWidth: 400 }}
							value={selectedLocation}
							onChange={(value) => {
								setSelectedLocation(value);
								const location = locations.find((l) => l.id === value);
								setSelectedLocationName(
									location?.address1 || location?.name || "Unknown",
								);
							}}
							loading={loadingLocations}
							disabled={loadingLocations}
							filterOption={(input, option) =>
								(option?.label ?? "")
									.toLowerCase()
									.includes(input.toLowerCase())
							}
							options={locations.map((loc: any) => ({
								label: loc.address1 || loc.name || "Unknown Location",
								value: loc.id,
							}))}
						/>
					</div>
				);

			case 1:
				return (
					<div className="step-content">
						<h3>Overview Photo</h3>
						<p>Take a wide-angle photo showing the asset in context.</p>

						{!overviewPhoto ? (
							<Dragger
								beforeUpload={(file) => handlePhotoSelect(file, "overview")}
								accept="image/*"
								showUploadList={false}
								className="photo-dragger"
							>
								<p className="ant-upload-drag-icon">
									<InboxOutlined />
								</p>
								<p className="ant-upload-text">
									Click or drag photo to this area
								</p>
								<p className="ant-upload-hint">
									Take or upload a wide-angle photo showing the asset in context
									<br />
									(HEIC/HEIF images will be automatically converted)
								</p>
							</Dragger>
						) : (
							<div className="photo-preview-container">
								<img src={overviewPhoto.preview} alt="Overview" />
								<Button
									icon={<DeleteOutlined />}
									danger
									onClick={() => handlePhotoRemove("overview")}
									className="remove-button"
								>
									Remove Photo
								</Button>
							</div>
						)}
					</div>
				);

			case 2:
				return (
					<div className="step-content">
						<h3>Manufacturer Plate Photo (Optional)</h3>
						<p>
							If accessible, take a close-up photo of the manufacturer/data
							plate.
						</p>

						{!manufacturerPhoto ? (
							<Space
								direction="vertical"
								size="large"
								style={{ width: "100%" }}
							>
								<Dragger
									beforeUpload={(file) =>
										handlePhotoSelect(file, "manufacturer")
									}
									accept="image/*"
									showUploadList={false}
									className="photo-dragger"
								>
									<p className="ant-upload-drag-icon">
										<InboxOutlined />
									</p>
									<p className="ant-upload-text">
										Click or drag photo to this area
									</p>
									<p className="ant-upload-hint">
										Take or upload a close-up photo of the manufacturer/data
										plate
										<br />
										(HEIC/HEIF images will be automatically converted)
									</p>
								</Dragger>
								<Button
									type="link"
									onClick={() => setSkipManufacturerModalVisible(true)}
								>
									Skip (No Manufacturer Plate)
								</Button>
							</Space>
						) : (
							<div className="photo-preview-container">
								<img src={manufacturerPhoto.preview} alt="Manufacturer Plate" />
								<Button
									icon={<DeleteOutlined />}
									danger
									onClick={() => handlePhotoRemove("manufacturer")}
									className="remove-button"
								>
									Remove Photo
								</Button>
							</div>
						)}
					</div>
				);

			case 3:
				return (
					<div className="step-content">
						<h3>Asset Tag Photo (Required)</h3>
						<p>
							Take a photo of the asset tag or barcode, or use Vision Tag to
							create a virtual tag.
						</p>

						{!assetTagPhoto ? (
							<Space
								direction="vertical"
								size="large"
								style={{ width: "100%" }}
							>
								<Dragger
									beforeUpload={(file) => handlePhotoSelect(file, "assetTag")}
									accept="image/*"
									showUploadList={false}
									className="photo-dragger"
								>
									<p className="ant-upload-drag-icon">
										<InboxOutlined />
									</p>
									<p className="ant-upload-text">
										Click or drag photo to this area
									</p>
									<p className="ant-upload-hint">
										Take or upload a photo of the asset tag or barcode
										<br />
										(HEIC/HEIF images will be automatically converted)
									</p>
								</Dragger>
								<div style={{ textAlign: "center", margin: "16px 0" }}>
									<span style={{ color: "#8c8c8c" }}>— OR —</span>
								</div>
								<Button
									type="primary"
									style={{ width: "100%" }}
									onClick={handleOpenVisionTag}
									disabled={!overviewPhoto}
								>
									Use Vision Tag (Virtual Tag)
								</Button>
							</Space>
						) : (
							<div className="photo-preview-container">
								<img src={assetTagPhoto.preview} alt="Asset Tag" />
								{visionTagMode && (
									<div
										style={{
											marginTop: "16px",
											padding: "12px",
											backgroundColor: "#f0f5ff",
											borderRadius: "4px",
											border: "1px solid #1890ff",
										}}
									>
										<CheckOutlined
											style={{ color: "#52c41a", marginRight: "8px" }}
										/>
										<strong>Vision Tag:</strong> {virtualAssetTag}
									</div>
								)}
								<Button
									icon={<DeleteOutlined />}
									danger
									onClick={() => {
										handlePhotoRemove("assetTag");
										if (visionTagMode) {
											handleCancelVisionTag();
										}
									}}
									className="remove-button"
								>
									Remove Photo
								</Button>
							</div>
						)}
					</div>
				);

			case 4:
				return (
					<div className="step-content">
						<h3>Review & Submit</h3>
						<p>Review your photos and submit for AI processing.</p>

						<div className="review-grid">
							<div className="review-item">
								<h4>Overview Photo</h4>
								{overviewPhoto?.preview && (
									<img
										src={overviewPhoto.preview}
										alt="Overview"
										className="review-thumbnail"
									/>
								)}
							</div>

							<div className="review-item">
								<h4>Manufacturer Plate</h4>
								{manufacturerPhoto?.preview ? (
									<img
										src={manufacturerPhoto.preview}
										alt="Manufacturer"
										className="review-thumbnail"
									/>
								) : (
									<p>Not provided</p>
								)}
							</div>

							<div className="review-item">
								<h4>Asset Tag</h4>
								{assetTagPhoto?.preview ? (
									<div>
										<img
											src={assetTagPhoto.preview}
											alt="Asset Tag"
											className="review-thumbnail"
										/>
										{visionTagMode && (
											<div
												style={{
													marginTop: "8px",
													padding: "8px",
													backgroundColor: "#f0f5ff",
													borderRadius: "4px",
													border: "1px solid #1890ff",
													fontSize: "12px",
												}}
											>
												{virtualAssetTag && (
													<div>
														<CheckOutlined style={{ color: "#52c41a", marginRight: "8px" }} />
														<strong>Vision Tag:</strong> {virtualAssetTag}
													</div>
												)}
											{capturingGPS && (
												<div style={{ marginTop: "4px", color: "#1890ff" }}>
													📍 Capturing live GPS coordinates...
												</div>
											)}
											{!capturingGPS && gpsLocation && (
												<div style={{ marginTop: "4px" }}>
													<CheckOutlined style={{ color: "#52c41a", marginRight: "8px" }} />
													<strong>GPS:</strong> {gpsLocation.latitude.toFixed(6)}, {gpsLocation.longitude.toFixed(6)} 
													<span style={{ color: "#666", marginLeft: "4px" }}>
														(±{gpsLocation.accuracy.toFixed(0)}m accuracy)
													</span>
												</div>
											)}
											{!capturingGPS && !gpsLocation && (
												<div style={{ marginTop: "4px", color: "#faad14" }}>
													⚠️ GPS not available - Visual Tag 2.0 enrollment will be skipped
												</div>
											)}
											</div>
										)}
									</div>
								) : (
									<p>Not provided</p>
								)}
							</div>

							{visionTagMode && visionTagLeftPhoto && (
								<div className="review-item">
									<h4>Left Side Photo</h4>
									<img
										src={visionTagLeftPhoto.preview}
										alt="Left Side"
										className="review-thumbnail"
									/>
								</div>
							)}

							{visionTagMode && visionTagRightPhoto && (
								<div className="review-item">
									<h4>Right Side Photo</h4>
									<img
										src={visionTagRightPhoto.preview}
										alt="Right Side"
										className="review-thumbnail"
									/>
								</div>
							)}
						</div>

						<div style={{ marginTop: "32px" }}>
							<h3>Additional Comments (Optional)</h3>
							<TextArea
								rows={4}
								placeholder="Add any additional notes or comments about this asset..."
								value={additionalComment}
								onChange={(e) => setAdditionalComment(e.target.value)}
								maxLength={1000}
								showCount
								style={{ marginTop: "16px" }}
							/>
						</div>
					</div>
				);

			default:
				return null;
		}
	};

	return (
		<div className="slim-form-capture-container">
			<Card title="Vision Capture" className="slim-form-card">
				<Steps current={currentStep} className="steps-container">
					<Step title="Location" />
					<Step title="Overview" />
					<Step title="Manufacturer Plate" />
					<Step title="Asset Tag" />
					<Step title="Review" />
				</Steps>

				{selectedLocationName && currentStep > 0 && (
					<div className="location-banner">
						<strong>Location:</strong> {selectedLocationName}
					</div>
				)}

				<div className="step-content-wrapper">{renderStepContent()}</div>

				<div className="step-actions">
					<Space>
						<Button
							icon={<CloseOutlined />}
							onClick={handleCancel}
							disabled={submitting || uploading}
						>
							Cancel
						</Button>

						{currentStep > 0 && currentStep < 4 && (
							<Button onClick={handleBack}>Back</Button>
						)}

						{currentStep < 4 && (
							<Button
								type="primary"
								onClick={handleNext}
								disabled={
									(currentStep === 2 && !manufacturerPhoto) ||
									(currentStep === 3 && !assetTagPhoto)
								}
							>
								Next
							</Button>
						)}

						{currentStep === 4 && (
							<>
								<Button
									onClick={handleBack}
									disabled={submitting || uploading || recentlySubmitted}
								>
									Back
								</Button>
								<Button
									type="primary"
									icon={<CheckOutlined />}
									onClick={handleSubmitAndStartNew}
									loading={submitting || uploading}
									disabled={submitting || uploading || recentlySubmitted}
								>
									Submit & Start New
								</Button>
								<Button
									type="primary"
									icon={<CheckOutlined />}
									onClick={handleSubmit}
									loading={submitting || uploading}
									disabled={submitting || uploading || recentlySubmitted}
								>
									Submit for Processing
								</Button>
							</>
						)}
					</Space>
				</div>
			</Card>

			{/* Skip Manufacturer Plate Modal */}
			<Modal
				title="Skip Manufacturer Plate"
				open={skipManufacturerModalVisible}
				onOk={handleSkipManufacturerPlate}
				onCancel={() => {
					setSkipManufacturerModalVisible(false);
					setSkipManufacturerReason("");
				}}
				okText="Continue"
				cancelText="Cancel"
			>
				<p style={{ marginBottom: 16 }}>
					Please select a reason for skipping the manufacturer plate photo. This
					will be added to the general notes.
				</p>
				<Select
					style={{ width: "100%" }}
					placeholder="Select a reason"
					value={skipManufacturerReason || undefined}
					onChange={(value) => setSkipManufacturerReason(value)}
					options={[
						{ label: "Plate Inaccessible", value: "Plate Inaccessible" },
						{ label: "Plate Not Present", value: "Plate Not Present" },
					]}
				/>
			</Modal>

			{/* Vision Tag Modal */}
			<Modal
				title={`Vision Tag - ${
					visionTagStep === 0
						? "Mark Asset Location"
						: visionTagStep === 1
							? "Capture Left Side"
							: "Capture Right Side"
				}`}
				open={visionTagModalVisible}
				onCancel={() => {
					setVisionTagModalVisible(false);
					setVisionTagBoundingBox(null);
					setVisionTagLeftPhoto(null);
					setVisionTagRightPhoto(null);
					setVisionTagGeneratedImage(null);
					setVisionTagStep(0);
					setIsDrawing(false);
					setStartPoint(null);
				}}
				width={800}
				footer={[
					<Button
						key="back"
						onClick={handleVisionTagPreviousStep}
						disabled={visionTagStep === 0}
					>
						Back
					</Button>,
					<Button
						key="next"
						type="primary"
						onClick={handleVisionTagNextStep}
						disabled={
							(visionTagStep === 0 && !visionTagBoundingBox) ||
							(visionTagStep === 1 && !visionTagLeftPhoto) ||
							(visionTagStep === 2 && !visionTagRightPhoto)
						}
					>
						{visionTagStep === 2 ? "Confirm & Generate Tag" : "Next"}
					</Button>,
					<Button
						key="cancel"
						onClick={() => {
							setVisionTagModalVisible(false);
							setVisionTagBoundingBox(null);
							setVisionTagLeftPhoto(null);
							setVisionTagRightPhoto(null);
							setVisionTagGeneratedImage(null);
							setVisionTagStep(0);
							setIsDrawing(false);
							setStartPoint(null);
						}}
					>
						Cancel
					</Button>,
				]}
			>
				<Steps current={visionTagStep} style={{ marginBottom: 24 }}>
					<Step title="Mark Asset" />
					<Step title="Left Side" />
					<Step title="Right Side" />
				</Steps>

				{visionTagStep === 0 && (
					<>
						<div style={{ marginBottom: 16 }}>
							<p style={{ marginBottom: 8 }}>
								<strong>Instructions:</strong> Draw a box around the asset in
								the photo. This will create a virtual tag to identify this
								specific asset.
							</p>
							<p style={{ color: "#8c8c8c", fontSize: "12px" }}>
								Click and drag to draw a bounding box. The box will be saved
								when you release the mouse button.
							</p>
						</div>

						<div
							style={{
								position: "relative",
								width: "100%",
								maxHeight: "500px",
								overflow: "auto",
								border: "2px solid #d9d9d9",
								borderRadius: "4px",
								cursor: "crosshair",
							}}
						>
							<img
								ref={imageRef}
								src={overviewPhoto?.preview}
								alt="Overview"
								style={{ display: "none" }}
								onLoad={(e) => {
									const img = e.target as HTMLImageElement;
									const canvas = canvasRef.current;
									if (canvas && img) {
										const maxWidth = 750;
										const scale = Math.min(1, maxWidth / img.naturalWidth);
										canvas.width = img.naturalWidth * scale;
										canvas.height = img.naturalHeight * scale;

										const ctx = canvas.getContext("2d");
										if (ctx) {
											ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
										}
									}
								}}
							/>
							<canvas
								ref={canvasRef}
								style={{ display: "block", maxWidth: "100%" }}
								onMouseDown={handleCanvasMouseDown}
								onMouseMove={handleCanvasMouseMove}
								onMouseUp={handleCanvasMouseUp}
							/>
						</div>

						{visionTagBoundingBox && (
							<div
								style={{
									marginTop: 16,
									padding: 12,
									backgroundColor: "#f0f5ff",
									borderRadius: 4,
									border: "1px solid #adc6ff",
								}}
							>
								<CheckOutlined style={{ color: "#52c41a", marginRight: 8 }} />
								Bounding box created successfully! Click "Next" to continue.
							</div>
						)}
					</>
				)}

				{visionTagStep === 1 && (
					<>
						<div style={{ marginBottom: 16 }}>
							<p style={{ marginBottom: 8 }}>
								<strong>Instructions:</strong> Take a photo of the asset from
								the LEFT side.
							</p>
							<p style={{ color: "#8c8c8c", fontSize: "12px" }}>
								This photo will help identify the asset from multiple angles.
							</p>
						</div>

						{!visionTagLeftPhoto ? (
							<Dragger
								beforeUpload={(file) =>
									handleVisionTagPhotoSelect(file, "left")
								}
								accept="image/*"
								showUploadList={false}
								className="photo-dragger"
							>
								<p className="ant-upload-drag-icon">
									<InboxOutlined />
								</p>
								<p className="ant-upload-text">
									Click or drag photo to this area
								</p>
								<p className="ant-upload-hint">
									Take or upload a photo of the LEFT side of the asset
									<br />
									(HEIC/HEIF images will be automatically converted)
								</p>
							</Dragger>
						) : (
							<div className="photo-preview-container">
								<img
									src={visionTagLeftPhoto.preview}
									alt="Left Side"
									style={{ maxWidth: "100%" }}
								/>
								<Button
									icon={<DeleteOutlined />}
									danger
									onClick={() => setVisionTagLeftPhoto(null)}
									style={{ marginTop: 16 }}
								>
									Remove Photo
								</Button>
							</div>
						)}
					</>
				)}

				{visionTagStep === 2 && (
					<>
						<div style={{ marginBottom: 16 }}>
							<p style={{ marginBottom: 8 }}>
								<strong>Instructions:</strong> Take a photo of the asset from
								the RIGHT side.
							</p>
							<p style={{ color: "#8c8c8c", fontSize: "12px" }}>
								This photo will help identify the asset from multiple angles.
							</p>
						</div>

						{!visionTagRightPhoto ? (
							<Dragger
								beforeUpload={(file) =>
									handleVisionTagPhotoSelect(file, "right")
								}
								accept="image/*"
								showUploadList={false}
								className="photo-dragger"
							>
								<p className="ant-upload-drag-icon">
									<InboxOutlined />
								</p>
								<p className="ant-upload-text">
									Click or drag photo to this area
								</p>
								<p className="ant-upload-hint">
									Take or upload a photo of the RIGHT side of the asset
									<br />
									(HEIC/HEIF images will be automatically converted)
								</p>
							</Dragger>
						) : (
							<div className="photo-preview-container">
								<img
									src={visionTagRightPhoto.preview}
									alt="Right Side"
									style={{ maxWidth: "100%" }}
								/>
								<Button
									icon={<DeleteOutlined />}
									danger
									onClick={() => setVisionTagRightPhoto(null)}
									style={{ marginTop: 16 }}
								>
									Remove Photo
								</Button>
							</div>
						)}
					</>
				)}
			</Modal>
		</div>
	);
};
